import React from "react";
import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuItem,
  NavbarMenuToggle,
  Link,
  Button,
  Divider,
  cn,
} from "@heroui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { blogUrl } from "../../lib/siteUrl";
import { buildSignupHref } from "../../lib/signupUrl";
import IntlSiteNoticeBar from "../../components/IntlSiteNoticeBar";
import { targetIndustries } from "./target-industries";

const navItems: Array<{ key: string; href: string; label: string }> = [
  { key: "home", href: "#home", label: "首页" },
  // { key: "station", href: "/station", label: "3Chat Station" },
  {
    key: "product",
    href: "#product-matrix",
    label: "产品",
  },
  // { key: "features", href: "#features", label: "卖点" },
  { key: "solution", href: "/solutions/science-demo", label: "解决方案" },
  // { key: "livechat", href: "/livechat/dtc-ecommerce", label: "体验中心" },
  { key: "pricing", href: "/price", label: "价格" },
  { key: "resource", href: "#resource", label: "资源" },
  { key: "about", href: "/about", label: "关于我们" },
];

type SolutionMenuItem = {
  title: string;
  href: string;
  external?: boolean;
};

const solutionMenuGroups: Array<{
  label: string;
  items: SolutionMenuItem[];
}> = [
  {
    label: "3Chat 私域客户运营",
    items: [
      {
        title: "3Chat × 豆包工作",
        href: "/stories/doubao-work",
        external: true,
      },
      {
        title: "3Chat × WorkBuddy",
        href: "/stories/workbuddy",
        external: true,
      },
    ],
  },
  {
    label: "行业解决方案",
    items: targetIndustries.map((industry) => ({
      title: industry.title,
      href: industry.href,
    })),
  },
];

const BasicNavbar = React.forwardRef<
  HTMLElement,
  React.ComponentProps<typeof Navbar>
>(({ classNames = {}, ...props }, ref) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const resolveSelectedKey = React.useCallback(
    (pathname: string, hash: string) => {
      // 解决方案类页面
      if (pathname.startsWith("/solutions/product")) return "product";
      if (pathname.startsWith("/solutions/")) return "solution";

      // 资源类页面
      if (pathname === "/about") return "about";
      if (pathname === "/blog") return "resource";
      if (pathname === "/price") return "pricing";

      // 单页锚点（仅在首页存在）
      if (hash === "#pricing") return "pricing";
      if (hash === "#faq") return "faq";
      if (hash === "#home") return "home";
      if (hash === "#product-matrix") return "product";

      // 兼容历史页面（如果进入 /3chatclaw，尽量高亮到产品入口）
      if (pathname.startsWith("/3chatclaw")) return "product";

      if (pathname.startsWith("/product/")) return "product";

      return "home";
    },
    []
  );

  const [selectedKey, setSelectedKey] = React.useState(() =>
    resolveSelectedKey(location.pathname, location.hash)
  );

  // 当路由/锚点变化时，同步导航高亮，避免“切到别的页面又回到首页高亮”
  React.useEffect(() => {
    setSelectedKey(resolveSelectedKey(location.pathname, location.hash));
  }, [location.pathname, location.hash, resolveSelectedKey]);

  const headerTrialSignupHrefWeb = React.useMemo(
    () => buildSignupHref("navbar", { search: location.search, device: "web" }),
    [location.search],
  );
  const headerTrialSignupHrefMobile = React.useMemo(
    () => buildSignupHref("navbar", { search: location.search, device: "mobile" }),
    [location.search],
  );

  const [isResourceOpen, setIsResourceOpen] = React.useState(false);
  const [isMobileResourceOpen, setIsMobileResourceOpen] = React.useState(false);
  const [isSolutionOpen, setIsSolutionOpen] = React.useState(false);
  const [isMobileSolutionOpen, setIsMobileSolutionOpen] =
    React.useState(false);

  const resourceHoverTimeout = React.useRef<number | null>(null);
  const solutionHoverTimeout = React.useRef<number | null>(null);

  const openResource = () => {
    if (resourceHoverTimeout.current) {
      window.clearTimeout(resourceHoverTimeout.current);
    }
    setIsSolutionOpen(false);
    setIsResourceOpen(true);
  };

  const closeResource = () => {
    if (resourceHoverTimeout.current) {
      window.clearTimeout(resourceHoverTimeout.current);
    }
    resourceHoverTimeout.current = window.setTimeout(() => {
      setIsResourceOpen(false);
    }, 80);
  };

  const openSolution = () => {
    if (solutionHoverTimeout.current) {
      window.clearTimeout(solutionHoverTimeout.current);
    }
    setIsResourceOpen(false);
    setIsSolutionOpen(true);
  };

  const closeSolution = () => {
    if (solutionHoverTimeout.current) {
      window.clearTimeout(solutionHoverTimeout.current);
    }
    solutionHoverTimeout.current = window.setTimeout(() => {
      setIsSolutionOpen(false);
    }, 80);
  };

  const scrollToProductMatrix = () => {
    setSelectedKey("product");
    if (location.pathname !== "/") {
      navigate("/#product-matrix");
      window.setTimeout(() => {
        document.getElementById("product-matrix")?.scrollIntoView({
          behavior: "smooth",
        });
      }, 100);
      return;
    }
    document.getElementById("product-matrix")?.scrollIntoView({
      behavior: "smooth",
    });
  };

  const handleNavClick = (e: React.MouseEvent, key: string, href: string) => {
    setSelectedKey(key);

    // Handle anchor links
    if (href.startsWith("#")) {
      e.preventDefault();
      const id = href.slice(1);

      // If not on home page, navigate to home first, then scroll
      if (location.pathname !== "/") {
        navigate(`/#${id}`);
        // Scroll after navigation
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        // Already on home page, just scroll
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
      return;
    }

    // For external links, let the browser handle the navigation
    // No need to prevent default behavior
  };

  return (
    <>
      <IntlSiteNoticeBar />
      <Navbar
      ref={ref}
      {...props}
      classNames={{
        base: cn(
          "fixed top-[var(--intl-notice-height,0px)] left-0 right-0 z-50 border-default-100 backdrop-blur-md backdrop-saturate-150",
          "bg-default-100/10 dark:bg-default-100/10",
          { "bg-default-200/50 dark:bg-default-100/50": isMenuOpen },
        ),
        wrapper: "w-full justify-center",
        item: "hidden md:flex",
        ...classNames,
      }}
      height="60px"
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
    >
      <NavbarBrand>
        <div className="rounded-full p-1">
          <img
            src="/images/3Chat.ai_Logo.svg"
            alt="3Chat.ai Logo"
            className="h-8 w-auto"
          />
        </div>
      </NavbarBrand>

      <NavbarContent justify="center">
        {navItems.map((item) =>
          item.key === "solution" ? (
            <NavbarItem
              key={`${item.key}-${item.label}`}
              isActive={selectedKey === item.key}
              className="relative data-[active=true]:font-medium"
              onMouseEnter={openSolution}
              onMouseLeave={closeSolution}
            >
              <button
                type="button"
                className={cn(
                  "text-sm outline-none hover:text-default-foreground",
                  isSolutionOpen || selectedKey === item.key
                    ? "text-default-foreground"
                    : "text-foreground/50"
                )}
              >
                {item.label}
              </button>

              <div
                className={cn(
                  "pointer-events-auto absolute left-1/2 top-full z-50 mt-1 flex w-max min-w-[240px] -translate-x-1/2 flex-col gap-2 rounded-xl border border-default-200 bg-background p-4 shadow-lg transition-opacity duration-150",
                  isSolutionOpen
                    ? "opacity-100"
                    : "pointer-events-none opacity-0"
                )}
              >
                {solutionMenuGroups.map((group) => (
                  <div key={group.label} className="flex w-full flex-col gap-1">
                    <p className="text-xs leading-[normal] text-default-400">
                      {group.label}
                    </p>
                    {group.items.map((entry) => (
                      <Link
                        key={entry.href}
                        className="block w-full rounded-lg p-2 text-sm leading-[normal] text-default-foreground no-underline hover:bg-default-100"
                        href={entry.href}
                        isExternal={entry.external}
                        onClick={(e) => {
                          if (entry.external) {
                            setIsSolutionOpen(false);
                            return;
                          }
                          handleNavClick(e, item.key, entry.href);
                        }}
                      >
                        {entry.title}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            </NavbarItem>
          ) : item.key === "resource" ? (
            <NavbarItem
              key={`${item.key}-${item.label}`}
              isActive={selectedKey === item.key}
              className="relative data-[active=true]:font-medium"
              onMouseEnter={openResource}
              onMouseLeave={closeResource}
            >
              <button
                type="button"
                className={cn(
                  "text-sm outline-none hover:text-default-foreground",
                  isResourceOpen || selectedKey === item.key
                    ? "text-default-foreground"
                    : "text-foreground/50"
                )}
              >
                {item.label}
              </button>

              <div
                className={cn(
                  "pointer-events-auto absolute left-1/2 top-full z-50 mt-1 -translate-x-1/2 min-w-[200px] rounded-xl border border-default-100 bg-background px-2 py-2 shadow-lg transition-opacity duration-150",
                  isResourceOpen ? "opacity-100" : "pointer-events-none opacity-0"
                )}
              >
                <Link
                  className="block rounded-lg px-3 py-2 text-sm text-default-600 hover:bg-default-100"
                  href="https://group.3chatai.cn/latest"
                  isExternal
                  onClick={(e) =>
                    handleNavClick(e, item.key, "https://group.3chatai.cn/latest")
                  }
                >
                  社区
                </Link>
                <Link
                  className="mt-1 block rounded-lg px-3 py-2 text-sm text-default-600 hover:bg-default-100 hover:text-default-foreground"
                  href={blogUrl()}
                  onClick={(e) => handleNavClick(e, item.key, "/blog")}
                >
                  博客
                </Link>
              </div>
            </NavbarItem>
          ) : (
            <NavbarItem
              key={`${item.key}-${item.label}`}
              isActive={selectedKey === item.key}
              className="data-[active=true]:font-medium"
            >
              <Link
                aria-current={selectedKey === item.key ? "page" : undefined}
                className={cn(
                  "text-sm hover:text-default-foreground",
                  selectedKey === item.key
                    ? "text-default-foreground"
                    : "text-foreground/50",
                )}
                href={
                  item.href.startsWith("#")
                    ? item.href
                    : `${window.location.origin}${item.href}${location.search}`
                }
                onClick={(e) => handleNavClick(e, item.key, item.href)}
              >
                {item.label}
              </Link>
            </NavbarItem>
          ),
        )}
      </NavbarContent>

      <NavbarContent className="hidden md:flex" justify="end">
        <NavbarItem className="ml-2 flex gap-2">
          <Button
            className="text-foreground/50 hover:text-default-foreground"
            radius="full"
            variant="light"
            onPress={() => {
              scrollToProductMatrix();
            }}
          >
            了解产品
          </Button>
          <Button
            as={Link}
            className="bg-default-foreground text-background font-medium"
            color="secondary"
            endContent={<ArrowRight className="w-4 h-4" />}
            href={headerTrialSignupHrefWeb}
            radius="full"
            variant="flat"
          >
            免费试用
          </Button>
        </NavbarItem>
      </NavbarContent>

      <NavbarMenuToggle className="text-default-400 md:hidden" />

      <NavbarMenu
        className="dark bg-default-100/10 shadow-medium top-[calc(var(--intl-notice-height,0px)+var(--navbar-height)-1px)] max-h-[calc(100dvh-var(--intl-notice-height,0px)-var(--navbar-height)+1px)] overflow-y-auto pt-6 pb-6 backdrop-blur-md backdrop-saturate-150"
        motionProps={{
          initial: { opacity: 0, y: -20 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: -20 },
          transition: {
            ease: "easeInOut",
            duration: 0.2,
          },
        }}
      >
        <NavbarMenuItem>
          <Button
            fullWidth
            variant="faded"
            onPress={() => {
              scrollToProductMatrix();
              setIsMenuOpen(false);
            }}
          >
            了解产品
          </Button>
        </NavbarMenuItem>
        <NavbarMenuItem className="mb-4">
          <Button
            fullWidth
            as={Link}
            className="bg-foreground text-background"
            href={headerTrialSignupHrefMobile}
          >
            免费试用
          </Button>
        </NavbarMenuItem>
        {navItems.map((item, index) =>
          item.key === "resource" ? (
            <NavbarMenuItem key={`${item.key}-${item.label}`} className="flex flex-col">
              <button
                type="button"
                className="mb-2 w-full text-left text-md text-default-foreground"
                onClick={() => setIsMobileResourceOpen((prev) => !prev)}
              >
                {item.label}
              </button>
              <div
                className={cn(
                  "mobile-submenu",
                  isMobileResourceOpen && "mobile-submenu-open"
                )}
              >
                <Link
                  className="mb-1 w-full text-md text-default-600"
                  href="https://group.3chatai.cn/latest"
                  isExternal
                  onClick={(e) => {
                    handleNavClick(e, item.key, "https://group.3chatai.cn/latest");
                    setIsMenuOpen(false);
                    setIsMobileResourceOpen(false);
                  }}
                >
                  社区
                </Link>
                <Link
                  className="mb-2 w-full text-md text-default-600"
                  href={blogUrl()}
                  onClick={(e) => {
                    handleNavClick(e, item.key, "/blog");
                    setIsMenuOpen(false);
                    setIsMobileResourceOpen(false);
                  }}
                >
                  博客
                </Link>
              </div>
              {index < navItems.length - 1 && <Divider className="opacity-50" />}
            </NavbarMenuItem>
          ) : item.key === "solution" ? (
            <NavbarMenuItem key={`${item.key}-${item.label}`} className="flex flex-col">
              <button
                type="button"
                className="mb-2 w-full text-left text-md text-default-foreground"
                onClick={() => setIsMobileSolutionOpen((prev) => !prev)}
              >
                {item.label}
              </button>
              <div
                className={cn(
                  "mobile-submenu",
                  isMobileSolutionOpen && "mobile-submenu-open mobile-submenu-tall"
                )}
              >
                {solutionMenuGroups.map((group, groupIdx) => (
                  <div
                    key={group.label}
                    className={cn(
                      "flex w-full flex-col",
                      groupIdx > 0 && "mt-3",
                    )}
                  >
                    <p className="mb-1 text-sm text-default-400">{group.label}</p>
                    {group.items.map((entry, idx) => (
                      <Link
                        key={entry.href}
                        className={cn(
                          "w-full text-md text-default-600",
                          idx < group.items.length - 1 && "mb-1",
                        )}
                        href={entry.href}
                        isExternal={entry.external}
                        onClick={(e) => {
                          if (!entry.external) {
                            handleNavClick(e, item.key, entry.href);
                          }
                          setIsMenuOpen(false);
                          setIsMobileSolutionOpen(false);
                        }}
                      >
                        {entry.title}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
              {index < navItems.length - 1 && <Divider className="opacity-50" />}
            </NavbarMenuItem>
          ) : (
            <NavbarMenuItem key={`${item.key}-${item.label}`}>
              <Link
                className="text-default-foreground mb-2 w-full text-md"
                href={
                  item.href.startsWith("#")
                    ? item.href
                    : `${window.location.origin}${item.href}${location.search}`
                }
                onClick={(e) => {
                  handleNavClick(e, item.key, item.href);
                  setIsMenuOpen(false);
                }}
              >
                {item.label}
              </Link>
              {index < navItems.length - 1 && (
                <Divider className="opacity-50" />
              )}
            </NavbarMenuItem>
          ),
        )}
      </NavbarMenu>
    </Navbar>
    </>
  );
});

BasicNavbar.displayName = "BasicNavbar";

export default BasicNavbar;
